import type { ReactNode } from 'react';

import { Icon } from '@eliadmin/core/src/components/nt/ui/icon';
import { cn } from '@eliadmin/core/src/utils/style-utils';

/**
 * Figma „Form / Input“ (Styleguide 164:12496) without the box: label (Body/M Bold, required `*`
 * #B42318), the control, a helper line (12 px #5D655D) or the error (#B42318 with a warning icon).
 * 6 px between the rows. `htmlFor` ties the label to the control; the helper/error get `id`s the
 * control references through `describedBy(id)`.
 */
export function FormField({
  id,
  label,
  required,
  hint,
  error,
  className,
  children,
}: {
  id: string;
  label?: ReactNode;
  required?: boolean;
  hint?: ReactNode;
  error?: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={cn('flex min-w-0 flex-col gap-1.5', className)}>
      {label ? (
        <label htmlFor={id} className="nt-body-m-bold flex gap-0.5 text-[#0D110D]">
          {label}
          {required ? (
            <span aria-hidden="true" className="text-[#B42318]">
              *
            </span>
          ) : null}
        </label>
      ) : null}
      {children}
      {error ? (
        <p id={`${id}-error`} className="nt-caption flex items-start gap-1 text-[#B42318]">
          <Icon name="warning-circle" size={14} className="mt-px shrink-0 text-[#D92D20]" />
          {error}
        </p>
      ) : hint ? (
        <p id={`${id}-hint`} className="nt-caption text-[#5D655D]">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

/** `aria-describedby` of a control inside `FormField`. */
export function describedBy(id: string, { error, hint }: { error?: string; hint?: ReactNode }) {
  return error ? `${id}-error` : hint ? `${id}-hint` : undefined;
}
